import React, { createContext, useContext, useState, useCallback } from "react";

import {
  CUSTOM_ERRORS_MAP,
  CustomCodes,
  ErrorResponse,
} from "./ErrorContextData";
import { ErrorDialog } from "components/MUIExtensions/ErrorDialog";

export interface ErrorContextProps {
  errorInfo?: ErrorResponse;
  isDialogOpen: boolean;
  setError: (error: ErrorResponse | CustomCodes) => void;
  clearError: () => void;
}

export const ErrorContext = createContext<ErrorContextProps>(null!);

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [errorInfo, setErrorInfo] = useState<ErrorResponse>();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  const setError = useCallback((errorIndicator) => {
    if (typeof errorIndicator === "string") {
      setErrorInfo(CUSTOM_ERRORS_MAP[errorIndicator]);
    } else {
      setErrorInfo(errorIndicator);
    }
    setIsDialogOpen(true);
  }, []);

  const clearError = useCallback(() => {
    setIsDialogOpen(false);
    setErrorInfo(undefined);
  }, []);

  return (
    <ErrorContext.Provider
      value={{
        errorInfo,
        isDialogOpen,
        setError,
        clearError,
      }}
    >
      {children}
      <ErrorDialog
        errorInfo={errorInfo}
        isDialogOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </ErrorContext.Provider>
  );
};

export const useErrorInfo = () => {
  const { errorInfo } = useContext(ErrorContext);
  return errorInfo;
};

export const useSetError = () => {
  const { setError } = useContext(ErrorContext);
  return setError;
};

export const useClearError = () => {
  const { clearError } = useContext(ErrorContext);
  return clearError;
};
